// src/components/dashboard/admin/AdminTransactionsPage.tsx

import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Search,
  Filter,
  ChevronDown,
  ChevronUp,
  Loader,
  Receipt,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  Download,
  ExternalLink,
  Calendar,
  TrendingUp,
  TrendingDown,
  Wallet,
  BookOpen,
  Users,
  Award,
  Menu,
  Sparkles,
  DollarSign,
  BarChart3,
  RefreshCw,
  Eye,
  User,
  Mail,
  CreditCard,
} from "lucide-react";
import { paymentApi, showError, showSuccess } from "../../../lib";
import { useAuth } from "../../../context/AuthContext";

interface Transaction {
  _id: string;
  user: {
    _id: string;
    name: string;
    email: string;
    avatar?: string;
  };
  course?: {
    _id: string;
    title: string;
    thumbnail?: string;
  };
  amount: number;
  currency: string;
  paymentMethod: string;
  transactionId: string;
  reference: string;
  status: "pending" | "completed" | "failed" | "refunded";
  type:
    | "course_enrollment"
    | "mentorship_session"
    | "certificate"
    | "subscription";
  description: string;
  metadata?: any;
  createdAt: string;
  updatedAt: string;
}

const AdminTransactionsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [typeFilter, setTypeFilter] = useState<string>("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [sortBy, setSortBy] = useState<
    "date-desc" | "date-asc" | "amount-desc" | "amount-asc"
  >("date-desc");
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [showStatusFilter, setShowStatusFilter] = useState(false);
  const [showTypeFilter, setShowTypeFilter] = useState(false);
  const [selectedTransaction, setSelectedTransaction] =
    useState<Transaction | null>(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [dateRange, setDateRange] = useState<{ start: string; end: string }>({
    start: "",
    end: "",
  });
  const [showDateFilter, setShowDateFilter] = useState(false);
  const limit = 10;

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    fetchTransactions();
  }, [currentPage, statusFilter, typeFilter, sortBy, dateRange]);

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const response = await paymentApi.getMyTransactions({
        page: currentPage,
        limit,
        status: statusFilter || undefined,
        type: typeFilter || undefined,
        startDate: dateRange.start || undefined,
        endDate: dateRange.end || undefined,
      });
      if (response.success) {
        setTransactions(response.data);
        setTotalPages(response.pagination.pages);
        setTotalItems(response.pagination.total);

        // Calculate total revenue
        const revenue = response.data
          .filter((t) => t.status === "completed")
          .reduce((sum, t) => sum + t.amount, 0);
        setTotalRevenue(revenue);
      }
    } catch (err) {
      console.error("Failed to fetch transactions:", err);
      showError("Failed to load transactions");
    } finally {
      setLoading(false);
    }
  };

  const getStatusConfig = (status: string) => {
    const configs: Record<
      string,
      { bg: string; text: string; icon: React.ReactNode; label: string }
    > = {
      pending: {
        bg: "bg-amber-50 border-amber-200",
        text: "text-amber-700",
        icon: <Clock className="w-3.5 h-3.5" />,
        label: "Pending",
      },
      completed: {
        bg: "bg-emerald-50 border-emerald-200",
        text: "text-emerald-700",
        icon: <CheckCircle className="w-3.5 h-3.5" />,
        label: "Completed",
      },
      failed: {
        bg: "bg-red-50 border-red-200",
        text: "text-red-700",
        icon: <XCircle className="w-3.5 h-3.5" />,
        label: "Failed",
      },
      refunded: {
        bg: "bg-purple-50 border-purple-200",
        text: "text-purple-700",
        icon: <AlertCircle className="w-3.5 h-3.5" />,
        label: "Refunded",
      },
    };
    return configs[status] || configs.pending;
  };

  const getTypeConfig = (type: string) => {
    const configs: Record<
      string,
      { icon: React.ReactNode; label: string; color: string }
    > = {
      course_enrollment: {
        icon: <BookOpen className="w-4 h-4" />,
        label: "Course",
        color: "text-blue-500",
      },
      mentorship_session: {
        icon: <Users className="w-4 h-4" />,
        label: "Mentorship",
        color: "text-purple-500",
      },
      certificate: {
        icon: <Award className="w-4 h-4" />,
        label: "Certificate",
        color: "text-amber-500",
      },
      subscription: {
        icon: <Wallet className="w-4 h-4" />,
        label: "Subscription",
        color: "text-emerald-500",
      },
    };
    return configs[type] || configs.course_enrollment;
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case "course_enrollment":
        return <BookOpen className="w-5 h-5 text-blue-500" />;
      case "mentorship_session":
        return <Users className="w-5 h-5 text-purple-500" />;
      case "certificate":
        return <Award className="w-5 h-5 text-amber-500" />;
      case "subscription":
        return <Wallet className="w-5 h-5 text-emerald-500" />;
      default:
        return <Receipt className="w-5 h-5 text-gray-500" />;
    }
  };

  const formatCurrency = (amount: number, currency: string = "XAF") => {
    return new Intl.NumberFormat("fr-CM", {
      style: "currency",
      currency: currency,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return {
      date: date.toLocaleDateString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
      }),
      time: date.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
      }),
      full: date.toLocaleString("en-US", {
        weekday: "short",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      }),
    };
  };

  const getStatusBadge = (status: string) => {
    const config = getStatusConfig(status);
    return (
      <span
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${config.bg} ${config.text}`}
      >
        {config.icon}
        {config.label}
      </span>
    );
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const clearFilters = () => {
    setStatusFilter("");
    setTypeFilter("");
    setSearchQuery("");
    setDateRange({ start: "", end: "" });
    setCurrentPage(1);
  };

  const exportTransactions = () => {
    // Create CSV content
    const headers = [
      "ID",
      "User",
      "Email",
      "Amount",
      "Status",
      "Type",
      "Payment Method",
      "Date",
      "Description",
    ];
    const rows = transactions.map((t) => [
      t.transactionId || t._id,
      t.user?.name || "Unknown",
      t.user?.email || "Unknown",
      t.amount,
      t.status,
      t.type,
      t.paymentMethod,
      new Date(t.createdAt).toLocaleDateString(),
      t.description,
    ]);

    const csvContent = [
      headers.join(","),
      ...rows.map((r) => r.join(",")),
    ].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `transactions_${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    showSuccess("Transactions exported successfully!");
  };

  const activeFilterCount =
    (statusFilter ? 1 : 0) +
    (typeFilter ? 1 : 0) +
    (searchQuery ? 1 : 0) +
    (dateRange.start || dateRange.end ? 1 : 0);

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px] bg-gradient-to-br from-orange-50 via-white to-orange-50/30">
        <div className="text-center">
          <Loader className="w-8 h-8 mx-auto mb-3 text-orange-500 animate-spin" />
          <p className="text-xs text-gray-500">Loading transactions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-4 bg-gradient-to-br from-orange-50 via-white to-orange-50/30 md:py-6">
      <div className="px-4 mx-auto max-w-7xl md:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-5">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate(-1)}
                className="p-2 text-gray-500 transition-colors rounded-lg hover:bg-gray-100"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-2">
                <div className="flex items-center justify-center w-10 h-10 rounded-lg shadow-sm bg-gradient-to-r from-purple-600 to-indigo-500">
                  <Receipt className="w-5 h-5 text-white" />
                </div>
                <h1 className="text-lg font-bold text-gray-900 md:text-xl">
                  Transaction Management
                </h1>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={fetchTransactions}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-all"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                Refresh
              </button>
              <button
                onClick={exportTransactions}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-gradient-to-r from-emerald-600 to-emerald-500 rounded-lg hover:shadow-md transition-all"
              >
                <Download className="w-3.5 h-3.5" />
                Export
              </button>
            </div>
          </div>
          <p className="text-xs text-gray-500 ml-14">
            Monitor and manage all platform transactions
          </p>
        </div>

        {/* Stats Summary */}
        <div className="grid grid-cols-2 gap-3 mb-5 sm:grid-cols-4">
          <div className="p-3 bg-white border border-gray-100 shadow-sm rounded-xl">
            <p className="text-[10px] text-gray-500">Total Revenue</p>
            <p className="text-sm font-bold text-emerald-600">
              {formatCurrency(totalRevenue)}
            </p>
          </div>
          <div className="p-3 bg-white border border-gray-100 shadow-sm rounded-xl">
            <p className="text-[10px] text-gray-500">Total Transactions</p>
            <p className="text-sm font-bold text-gray-900">{totalItems}</p>
          </div>
          <div className="p-3 bg-white border border-gray-100 shadow-sm rounded-xl">
            <p className="text-[10px] text-gray-500">Completed</p>
            <p className="text-sm font-bold text-emerald-600">
              {transactions.filter((t) => t.status === "completed").length}
            </p>
          </div>
          <div className="p-3 bg-white border border-gray-100 shadow-sm rounded-xl">
            <p className="text-[10px] text-gray-500">Pending / Failed</p>
            <p className="text-sm font-bold text-amber-600">
              {
                transactions.filter(
                  (t) => t.status === "pending" || t.status === "failed",
                ).length
              }
            </p>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-5">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[140px]">
              <Search className="absolute w-4 h-4 text-gray-400 -translate-y-1/2 left-3 top-1/2" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by user, email, ID..."
                className="w-full py-2 pl-9 pr-4 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
              />
            </div>

            {/* Status Filter */}
            <div className="relative">
              <button
                onClick={() => setShowStatusFilter(!showStatusFilter)}
                className={`flex items-center gap-1.5 px-3 py-2 text-sm border rounded-lg transition-all ${
                  statusFilter
                    ? "bg-purple-50 border-purple-200 text-purple-600"
                    : "bg-white border-gray-200 text-gray-600 hover:border-gray-300"
                }`}
              >
                <Filter className="w-4 h-4" />
                <span>Status</span>
                {statusFilter && (
                  <span className="w-4 h-4 text-xs bg-purple-500 text-white rounded-full flex items-center justify-center">
                    •
                  </span>
                )}
                <ChevronDown
                  className={`w-3.5 h-3.5 transition-transform ${showStatusFilter ? "rotate-180" : ""}`}
                />
              </button>
              {showStatusFilter && (
                <div className="absolute left-0 z-10 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg min-w-[150px] overflow-hidden">
                  {["", "pending", "completed", "failed", "refunded"].map(
                    (s) => (
                      <button
                        key={s || "all"}
                        onClick={() => {
                          setStatusFilter(s);
                          setShowStatusFilter(false);
                          setCurrentPage(1);
                        }}
                        className={`w-full px-3 py-2 text-left text-sm hover:bg-purple-50 transition-colors ${
                          statusFilter === s
                            ? "bg-purple-50 text-purple-600 font-medium"
                            : "text-gray-700"
                        }`}
                      >
                        {s
                          ? s.charAt(0).toUpperCase() + s.slice(1)
                          : "All Status"}
                      </button>
                    ),
                  )}
                </div>
              )}
            </div>

            {/* Type Filter */}
            <div className="relative">
              <button
                onClick={() => setShowTypeFilter(!showTypeFilter)}
                className={`flex items-center gap-1.5 px-3 py-2 text-sm border rounded-lg transition-all ${
                  typeFilter
                    ? "bg-indigo-50 border-indigo-200 text-indigo-600"
                    : "bg-white border-gray-200 text-gray-600 hover:border-gray-300"
                }`}
              >
                <BookOpen className="w-4 h-4" />
                <span>Type</span>
                {typeFilter && (
                  <span className="w-4 h-4 text-xs bg-indigo-500 text-white rounded-full flex items-center justify-center">
                    •
                  </span>
                )}
                <ChevronDown
                  className={`w-3.5 h-3.5 transition-transform ${showTypeFilter ? "rotate-180" : ""}`}
                />
              </button>
              {showTypeFilter && (
                <div className="absolute left-0 z-10 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg min-w-[150px] overflow-hidden">
                  {[
                    "",
                    "course_enrollment",
                    "mentorship_session",
                    "certificate",
                    "subscription",
                  ].map((t) => (
                    <button
                      key={t || "all"}
                      onClick={() => {
                        setTypeFilter(t);
                        setShowTypeFilter(false);
                        setCurrentPage(1);
                      }}
                      className={`w-full px-3 py-2 text-left text-sm hover:bg-indigo-50 transition-colors ${
                        typeFilter === t
                          ? "bg-indigo-50 text-indigo-600 font-medium"
                          : "text-gray-700"
                      }`}
                    >
                      {t
                        ? t.replace("_", " ").charAt(0).toUpperCase() +
                          t.slice(1).replace("_", " ")
                        : "All Types"}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Date Filter */}
            <div className="relative">
              <button
                onClick={() => setShowDateFilter(!showDateFilter)}
                className={`flex items-center gap-1.5 px-3 py-2 text-sm border rounded-lg transition-all ${
                  dateRange.start || dateRange.end
                    ? "bg-amber-50 border-amber-200 text-amber-600"
                    : "bg-white border-gray-200 text-gray-600 hover:border-gray-300"
                }`}
              >
                <Calendar className="w-4 h-4" />
                <span>Date</span>
                {(dateRange.start || dateRange.end) && (
                  <span className="w-4 h-4 text-xs bg-amber-500 text-white rounded-full flex items-center justify-center">
                    •
                  </span>
                )}
                <ChevronDown
                  className={`w-3.5 h-3.5 transition-transform ${showDateFilter ? "rotate-180" : ""}`}
                />
              </button>
              {showDateFilter && (
                <div className="absolute left-0 z-10 mt-1 p-3 bg-white border border-gray-200 rounded-lg shadow-lg min-w-[200px]">
                  <div className="space-y-2">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Start Date
                      </label>
                      <input
                        type="date"
                        value={dateRange.start}
                        onChange={(e) =>
                          setDateRange((prev) => ({
                            ...prev,
                            start: e.target.value,
                          }))
                        }
                        className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/20"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        End Date
                      </label>
                      <input
                        type="date"
                        value={dateRange.end}
                        onChange={(e) =>
                          setDateRange((prev) => ({
                            ...prev,
                            end: e.target.value,
                          }))
                        }
                        className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/20"
                      />
                    </div>
                    <button
                      onClick={() => {
                        setDateRange({ start: "", end: "" });
                        setShowDateFilter(false);
                        setCurrentPage(1);
                      }}
                      className="w-full py-1.5 text-xs text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      Clear Dates
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Sort */}
            <div className="relative ml-auto">
              <button
                onClick={() => setShowSortMenu(!showSortMenu)}
                className="flex items-center gap-1.5 px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg hover:border-gray-300 transition-all"
              >
                <TrendingUp className="w-4 h-4 text-gray-400" />
                <span className="hidden sm:inline">
                  {sortBy === "date-desc"
                    ? "Newest"
                    : sortBy === "date-asc"
                      ? "Oldest"
                      : sortBy === "amount-desc"
                        ? "Highest"
                        : "Lowest"}
                </span>
                <ChevronDown
                  className={`w-3.5 h-3.5 transition-transform ${showSortMenu ? "rotate-180" : ""}`}
                />
              </button>
              {showSortMenu && (
                <div className="absolute right-0 z-10 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg min-w-[150px] overflow-hidden">
                  {[
                    { value: "date-desc", label: "Newest First" },
                    { value: "date-asc", label: "Oldest First" },
                    { value: "amount-desc", label: "Highest Amount" },
                    { value: "amount-asc", label: "Lowest Amount" },
                  ].map((s) => (
                    <button
                      key={s.value}
                      onClick={() => {
                        setSortBy(s.value as typeof sortBy);
                        setShowSortMenu(false);
                        setCurrentPage(1);
                      }}
                      className={`w-full px-3 py-2 text-left text-sm hover:bg-purple-50 transition-colors ${
                        sortBy === s.value
                          ? "bg-purple-50 text-purple-600 font-medium"
                          : "text-gray-700"
                      }`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Clear Filters */}
            {activeFilterCount > 0 && (
              <button
                onClick={clearFilters}
                className="px-3 py-2 text-xs font-medium text-gray-500 bg-gray-100 rounded-lg hover:bg-gray-200 transition-all"
              >
                Clear ({activeFilterCount})
              </button>
            )}
          </div>
        </div>

        {/* Transactions List */}
        {transactions.length === 0 ? (
          <div className="py-12 text-center bg-white border border-gray-100 rounded-xl">
            <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full">
              <Receipt className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="mb-2 text-sm font-medium text-gray-900">
              No transactions found
            </h3>
            <p className="text-xs text-gray-500">
              {searchQuery ||
              statusFilter ||
              typeFilter ||
              dateRange.start ||
              dateRange.end
                ? "Try adjusting your filters"
                : "Transactions will appear here"}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {transactions.map((transaction) => {
              const statusConfig = getStatusConfig(transaction.status);
              const typeConfig = getTypeConfig(transaction.type);
              const formattedDate = formatDate(transaction.createdAt);

              return (
                <div
                  key={transaction._id}
                  className="overflow-hidden bg-white border border-gray-100 shadow-sm rounded-xl hover:shadow-md transition-all"
                >
                  <div className="p-4">
                    <div className="flex flex-wrap items-center gap-3">
                      {/* Icon */}
                      <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-gray-50 shrink-0">
                        {getTypeIcon(transaction.type)}
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="text-sm font-semibold text-gray-900 truncate">
                            {transaction.description ||
                              `${typeConfig.label} Payment`}
                          </h4>
                          {getStatusBadge(transaction.status)}
                        </div>
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1">
                          <span className="text-xs text-gray-500">
                            {formattedDate.date} at {formattedDate.time}
                          </span>
                          <span className="text-xs text-gray-400">•</span>
                          <span className="text-xs text-gray-500 flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {transaction.user?.name || "Unknown"}
                          </span>
                          <span className="text-xs text-gray-400">•</span>
                          <span className="text-xs text-gray-500 truncate max-w-[120px]">
                            {transaction.transactionId?.slice(0, 12) || "N/A"}
                          </span>
                        </div>
                      </div>

                      {/* Amount & Actions */}
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p
                            className={`text-sm font-bold ${transaction.status === "completed" ? "text-emerald-600" : transaction.status === "failed" ? "text-red-600" : "text-gray-600"}`}
                          >
                            {formatCurrency(
                              transaction.amount,
                              transaction.currency,
                            )}
                          </p>
                          <p className="text-[10px] text-gray-400 uppercase">
                            {transaction.paymentMethod || "Unknown"}
                          </p>
                        </div>
                        <button
                          onClick={() => {
                            setSelectedTransaction(transaction);
                            setShowDetailModal(true);
                          }}
                          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                          title="View details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex flex-col items-center gap-3 mt-6">
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className={`p-2 rounded-lg border transition-colors ${
                  currentPage === 1
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200"
                    : "bg-white text-gray-600 hover:bg-gray-50 border-gray-200"
                }`}
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${
                        currentPage === pageNum
                          ? "bg-purple-600 text-white"
                          : "bg-white text-gray-600 hover:bg-gray-50 border border-gray-200"
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`p-2 rounded-lg border transition-colors ${
                  currentPage === totalPages
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed border-gray-200"
                    : "bg-white text-gray-600 hover:bg-gray-50 border-gray-200"
                }`}
              >
                <ArrowLeft className="w-4 h-4 rotate-180" />
              </button>
            </div>
            <div className="text-xs text-gray-400">
              Showing {(currentPage - 1) * limit + 1} -{" "}
              {Math.min(currentPage * limit, totalItems)} of {totalItems}{" "}
              transactions
            </div>
          </div>
        )}
      </div>

      {/* Transaction Detail Modal */}
      {showDetailModal && selectedTransaction && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
          onClick={(e) =>
            e.target === e.currentTarget && setShowDetailModal(false)
          }
        >
          <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden animate-fade-in-up">
            <div className="px-5 py-4 bg-gradient-to-r from-purple-600 to-indigo-500">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold text-white">
                  Transaction Details
                </h2>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="p-1 text-white/80 hover:text-white"
                >
                  <XCircle className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="p-5 space-y-4">
              <div className="text-center pb-4 border-b border-gray-100">
                <div className="flex items-center justify-center mb-2">
                  {getTypeIcon(selectedTransaction.type)}
                </div>
                <p className="text-xl font-bold text-gray-900">
                  {formatCurrency(
                    selectedTransaction.amount,
                    selectedTransaction.currency,
                  )}
                </p>
                <p className="text-sm text-gray-500">
                  {selectedTransaction.description}
                </p>
                {getStatusBadge(selectedTransaction.status)}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-[10px] text-gray-400">Transaction ID</p>
                  <p className="text-xs font-medium text-gray-700 truncate">
                    {selectedTransaction.transactionId || "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] text-gray-400">Reference</p>
                  <p className="text-xs font-medium text-gray-700 truncate">
                    {selectedTransaction.reference || "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] text-gray-400">Payment Method</p>
                  <p className="text-xs font-medium text-gray-700 capitalize">
                    {selectedTransaction.paymentMethod || "Unknown"}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] text-gray-400">Date</p>
                  <p className="text-xs font-medium text-gray-700">
                    {formatDate(selectedTransaction.createdAt).full}
                  </p>
                </div>
                <div className="col-span-2">
                  <p className="text-[10px] text-gray-400">User</p>
                  <p className="text-xs font-medium text-gray-700">
                    {selectedTransaction.user?.name || "Unknown"}
                  </p>
                  <p className="text-xs text-gray-500">
                    {selectedTransaction.user?.email || "No email"}
                  </p>
                </div>
                {selectedTransaction.course && (
                  <div className="col-span-2">
                    <p className="text-[10px] text-gray-400">Course</p>
                    <p className="text-xs font-medium text-gray-700">
                      {selectedTransaction.course.title}
                    </p>
                  </div>
                )}
              </div>

              {selectedTransaction.metadata && (
                <div className="pt-2 border-t border-gray-100">
                  <p className="text-[10px] text-gray-400 mb-1">
                    Additional Details
                  </p>
                  <pre className="text-xs text-gray-600 bg-gray-50 p-2 rounded-lg overflow-x-auto max-h-32">
                    {JSON.stringify(selectedTransaction.metadata, null, 2)}
                  </pre>
                </div>
              )}
            </div>
            <div className="px-5 py-3 bg-gray-50 border-t border-gray-100">
              <button
                onClick={() => setShowDetailModal(false)}
                className="w-full py-2 text-sm font-medium text-center text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fade-in-up {
          from { opacity: 0; transform: translateY(20px) scale(0.95); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        .animate-fade-in-up {
          animation: fade-in-up 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default AdminTransactionsPage;
